import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Autocomplete,
  Box,
  ListItemIcon,
  ListItemText,
  createFilterOptions,
} from '@mui/material';
import type { StaffUser } from '@/hooks/useStaffUsers';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CircleIcon from '@mui/icons-material/Circle';
import { FormDialog } from '@/components/common/FormDialog';
import { useFormDirty } from '@/hooks/useFormDirty';
import { useNotify } from '@/stores/notificationStore';
import {
  useCreateShiftPattern,
  useUpdateShiftPattern,
  type ShiftPattern,
  type CreateShiftSlotData,
  type CreateShiftGroupData,
} from '@/hooks/useShifts';
import { useStaffUsers } from '@/hooks/useStaffUsers';
import { shiftSlotColors } from '@/styles/theme';

const WEEKDAY_KEYS = [1, 2, 3, 4, 5, 6, 7] as const;

const ROLE_ORDER: Record<string, number> = { mechanic: 0, manager: 1, admin: 2, owner: 3, reception: 4, developer: 5 };

const staffFilterOptions = createFilterOptions<StaffUser>({
  stringify: (option) => `${option.firstName} ${option.lastName} ${option.email}`,
});

interface ShiftPatternFormDialogProps {
  open: boolean;
  onClose: () => void;
  pattern?: ShiftPattern | null;
}

export function ShiftPatternFormDialog({ open, onClose, pattern }: ShiftPatternFormDialogProps) {
  const { t } = useTranslation('shifts');
  const { t: tc } = useTranslation('common');
  const { t: ts } = useTranslation('settings');
  const notify = useNotify();
  const createPattern = useCreateShiftPattern();
  const updatePattern = useUpdateShiftPattern();
  const { data: staffData } = useStaffUsers({ isActive: true });
  const { isDirty, markDirty } = useFormDirty(open);
  const allStaff = useMemo(
    () => [...(staffData?.users ?? [])].sort((a, b) => (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99)),
    [staffData],
  );

  const isEdit = !!pattern;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rotationType, setRotationType] = useState<'fixed' | 'weekly_rotation'>('weekly_rotation');
  const [rotationPeriodWeeks, setRotationPeriodWeeks] = useState(4);
  const [referenceDate, setReferenceDate] = useState('');
  const [slots, setSlots] = useState<CreateShiftSlotData[]>([]);
  const [groups, setGroups] = useState<(CreateShiftGroupData & { id?: string })[]>([]);

  useEffect(() => {
    if (pattern) {
      setName(pattern.name);
      setDescription(pattern.description ?? '');
      setRotationType(pattern.rotationType);
      setRotationPeriodWeeks(pattern.rotationPeriodWeeks);
      setReferenceDate(pattern.referenceDate.slice(0, 10));
      setSlots(
        pattern.slots.map((s) => ({
          position: s.position,
          startTime: s.startTime,
          endTime: s.endTime,
          daysOfWeek: s.daysOfWeek,
          color: s.color,
        }))
      );
      setGroups(
        pattern.groups.map((g) => ({
          id: g.id,
          name: g.name,
          position: g.position,
          memberUserIds: g.members.map((m) => m.userId),
        }))
      );
    } else {
      setName('');
      setDescription('');
      setRotationType('weekly_rotation');
      setRotationPeriodWeeks(4);
      setReferenceDate(new Date().toISOString().slice(0, 10));
      setSlots([
        { position: 0, startTime: '07:00', endTime: '15:00', daysOfWeek: [1, 2, 3, 4, 5], color: shiftSlotColors[0] },
      ]);
      setGroups([
        { name: '', position: 0, memberUserIds: [] },
      ]);
    }
  }, [pattern, open]);

  const availableStaffByGroup = useMemo(() => {
    const map = new Map<number, StaffUser[]>();
    groups.forEach((_g, idx) => {
      const otherIds = new Set<string>();
      groups.forEach((other, otherIdx) => {
        if (otherIdx !== idx) {
          (other.memberUserIds ?? []).forEach((id) => otherIds.add(id));
        }
      });
      map.set(idx, allStaff.filter((u) => !otherIds.has(u.id)));
    });
    return map;
  }, [groups, allStaff]);

  const addSlot = () => {
    const nextPos = slots.length;
    setSlots([...slots, {
      position: nextPos,
      startTime: '08:00',
      endTime: '16:00',
      daysOfWeek: [1, 2, 3, 4, 5],
      color: shiftSlotColors[nextPos % shiftSlotColors.length],
    }]);
    markDirty();
  };

  const removeSlot = (index: number) => {
    setSlots(slots.filter((_, i) => i !== index).map((s, i) => ({ ...s, position: i })));
    markDirty();
  };

  const updateSlot = (index: number, field: keyof CreateShiftSlotData, value: unknown) => {
    setSlots(slots.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
    markDirty();
  };

  const addGroup = () => {
    const nextPos = groups.length;
    setGroups([...groups, { name: '', position: Math.min(nextPos, slots.length - 1), memberUserIds: [] }]);
    markDirty();
  };

  const removeGroup = (index: number) => {
    setGroups(groups.filter((_, i) => i !== index));
    markDirty();
  };

  const updateGroup = (index: number, field: string, value: unknown) => {
    setGroups(groups.map((g, i) => (i === index ? { ...g, [field]: value } : g)));
    markDirty();
  };

  const toggleWeekday = (slotIndex: number, day: number) => {
    const slot = slots[slotIndex];
    if (!slot) return;
    const days = slot.daysOfWeek ?? [1, 2, 3, 4, 5];
    const newDays = days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort();
    updateSlot(slotIndex, 'daysOfWeek', newDays);
  };

  const handleSubmit = async () => {
    if (!name.trim() || slots.length === 0 || groups.length === 0) return;

    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      rotationType,
      rotationPeriodWeeks,
      referenceDate: new Date(referenceDate).toISOString(),
      slots,
      groups: groups.map((g) => ({
        name: g.name,
        position: g.position,
        memberUserIds: g.memberUserIds,
      })),
    };

    try {
      if (isEdit && pattern) {
        await updatePattern.mutateAsync({ id: pattern.id, data: payload });
        notify.success(t('patternUpdated'), t('patternUpdatedDesc'));
      } else {
        await createPattern.mutateAsync(payload);
        notify.success(t('patternCreated'), t('patternCreatedDesc'));
      }
      onClose();
    } catch {
      notify.error(tc('error'), t('errorSaving'));
    }
  };

  const isPending = createPattern.isPending || updatePattern.isPending;

  const slotLabel = (slotIndex: number) => {
    const s = slots[slotIndex];
    if (!s) return '';
    return `${s.startTime} – ${s.endTime}`;
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEdit ? t('editPattern') : t('createPattern')}
      isDirty={isDirty}
      onSubmit={() => void handleSubmit()}
      submitDisabled={!name.trim() || slots.length === 0 || groups.length === 0}
      isLoading={isPending}
      maxWidth="md"
      fullWidth
    >
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Basic info */}
          <TextField
            label={t('patternName')}
            placeholder={t('patternNamePlaceholder')}
            value={name}
            onChange={(e) => { setName(e.target.value); markDirty(); }}
            fullWidth
            required
          />

          <TextField
            label={t('description')}
            value={description}
            onChange={(e) => { setDescription(e.target.value); markDirty(); }}
            multiline
            rows={2}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>{t('rotationType')}</InputLabel>
              <Select
                value={rotationType}
                label={t('rotationType')}
                onChange={(e) => { setRotationType(e.target.value as typeof rotationType); markDirty(); }}
              >
                <MenuItem value="fixed">{t('fixed')}</MenuItem>
                <MenuItem value="weekly_rotation">{t('weeklyRotation')}</MenuItem>
              </Select>
            </FormControl>

            {rotationType === 'weekly_rotation' && (
              <TextField
                label={t('rotationPeriod')}
                type="number"
                value={rotationPeriodWeeks}
                onChange={(e) => { setRotationPeriodWeeks(Number(e.target.value)); markDirty(); }}
                slotProps={{ htmlInput: { min: 1, max: 52 } }}
                fullWidth
              />
            )}

            <TextField
              label={t('referenceDate')}
              type="date"
              value={referenceDate}
              onChange={(e) => { setReferenceDate(e.target.value); markDirty(); }}
              fullWidth
              helperText={t('referenceDateHint')}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          {/* Slots */}
          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={600}>{t('slots')}</Typography>
            <Tooltip title={t('addSlot')}>
              <IconButton onClick={addSlot} size="small" color="primary" aria-label={t('addSlot')}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          {slots.map((slot, idx) => (
            <Stack key={idx} spacing={1} sx={{ pl: 1, borderLeft: 3, borderColor: slot.color }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label={t('startTime')}
                  type="time"
                  value={slot.startTime}
                  onChange={(e) => updateSlot(idx, 'startTime', e.target.value)}
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  label={t('endTime')}
                  type="time"
                  value={slot.endTime}
                  onChange={(e) => updateSlot(idx, 'endTime', e.target.value)}
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  label={t('color')}
                  type="color"
                  value={slot.color}
                  onChange={(e) => updateSlot(idx, 'color', e.target.value)}
                  size="small"
                  sx={{ width: 80 }}
                />
                {slots.length > 1 && (
                  <Tooltip title={t('removeSlot')}>
                    <IconButton onClick={() => removeSlot(idx)} size="small" color="error" aria-label={t('removeSlot')}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {WEEKDAY_KEYS.map((day) => (
                  <Chip
                    key={day}
                    label={t(`weekdays.${day}`)}
                    size="small"
                    onClick={() => toggleWeekday(idx, day)}
                    color={(slot.daysOfWeek ?? []).includes(day) ? 'primary' : 'default'}
                    variant={(slot.daysOfWeek ?? []).includes(day) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Stack>
          ))}

          {/* Groups */}
          <Divider />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={600}>{t('groups')}</Typography>
            <Tooltip title={t('addGroup')}>
              <IconButton onClick={addGroup} size="small" color="primary" aria-label={t('addGroup')}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          {groups.map((group, idx) => (
            <Stack key={idx} spacing={1.5} sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label={t('groupName')}
                  value={group.name}
                  onChange={(e) => updateGroup(idx, 'name', e.target.value)}
                  size="small"
                  fullWidth
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>{t('startingSlot')}</InputLabel>
                  <Select
                    value={group.position}
                    label={t('startingSlot')}
                    onChange={(e) => updateGroup(idx, 'position', Number(e.target.value))}
                    renderValue={(val) => slotLabel(val as number)}
                  >
                    {slots.map((s, slotIdx) => (
                      <MenuItem key={slotIdx} value={slotIdx}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CircleIcon sx={{ color: s.color, fontSize: 16 }} />
                        </ListItemIcon>
                        <ListItemText primary={`${s.startTime} – ${s.endTime}`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {groups.length > 1 && (
                  <Tooltip title={t('removeGroup')}>
                    <IconButton onClick={() => removeGroup(idx)} size="small" color="error" aria-label={t('removeGroup')}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Autocomplete
                multiple
                options={availableStaffByGroup.get(idx) ?? []}
                getOptionLabel={(u) => `${u.firstName} ${u.lastName}`}
                filterOptions={staffFilterOptions}
                groupBy={(option) => ts(`roles.${option.role}`)}
                value={allStaff.filter((u) => (group.memberUserIds ?? []).includes(u.id))}
                onChange={(_, vals) => updateGroup(idx, 'memberUserIds', vals.map((v) => v.id))}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('groupMembers')} size="small" />
                )}
                size="small"
              />
            </Stack>
          ))}
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
